<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn :to="{ name: 'student-add-license' }" exact variant="primary"> Add License </b-btn>
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="licenses"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            show-empty
            empty-text="Add your CNA or medication aide license so it can be verified."
          >
            <template #cell(credential_type)="data">
              <strong>{{ credentialTypeLabel(data.item.credential_type) }}</strong>
            </template>

            <template #cell(state)="data">
              {{ data.item.state || '—' }}
            </template>

            <template #cell(status)="data">
              <b-badge
                :class="`border border-${statusMeta(data.item.status).variant} text-${
                  statusMeta(data.item.status).variant
                }`"
                variant="none"
                >{{ statusMeta(data.item.status).label }}</b-badge
              >
            </template>

            <template #cell(documents)="data">
              <b-badge variant="none" class="border border-secondary text-secondary">
                {{ (data.item.documents || []).length }}
              </b-badge>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret>
                <template #button-content>
                  <i class="material-icons icon-24pt text-info">more_vert</i>
                </template>
                <b-dropdown-item :to="{ name: 'student-edit-license', params: { id: data.item.id } }">
                  <md-icon class="text-info mr-2">edit</md-icon>
                  {{ $t('generalMsgs.edit') }}
                </b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item @click="removeLicense(data.item.id)">
                  <md-icon class="text-danger mr-2">delete</md-icon>
                  {{ $t('generalMsgs.remove') }}
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalLicenses"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="licenses-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import { STUDENT_LICENSE_STATUS_META, credentialTypeLabel } from '../../../common/licenseVerification';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: {
    MdIcon,
    PageHeader,
    PageSeparator,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'My Licenses',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalLicenses: 0,
      licenses: [],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'My Licenses', active: true },
      ];
    },

    tableColumns() {
      return [
        { key: 'actions', label: '', class: 'text-left', thStyle: { minWidth: '90px' } },
        { key: 'credential_type', label: 'License' },
        { key: 'state', label: 'State' },
        { key: 'license_number', label: 'License Number' },
        { key: 'status', label: 'Status' },
        { key: 'documents', label: 'Proof' },
      ];
    },
  },

  methods: {
    ...mapActions('licenseVerification', ['getAllLicenseVerifications', 'deleteLicenseVerification']),
    credentialTypeLabel,

    statusMeta(status) {
      return STUDENT_LICENSE_STATUS_META[status] || STUDENT_LICENSE_STATUS_META.pending;
    },

    async removeLicense(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          'Please confirm that you want to remove this license.',
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteLicenseVerification(id);
          this.fetchLicenses();
          this.makeToast({ variant: 'success', msg: 'License removed.' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async fetchLicenses(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllLicenseVerifications({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
        });
        this.licenses = response.data.results;
        this.currentPage = pageNum;
        this.totalLicenses = response.data.count;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchLicenses(pageNum);
    },
  },

  async mounted() {
    await this.fetchLicenses();
  },
};
</script>
